import React, { useState, useEffect, useCallback } from 'react'
import { Modal } from './ui'
import { QuickAllocateModalMeta, API_BASE_URL } from '../data'
import { QuickAllocateModalMetaProps, AreaAvailability, ShiftAvailability } from '../data/types'
import { ClockIcon, LocationIcon, CheckCircleIcon, XCircleIcon, WarningIcon } from '../icons'

type SortOption = 'urgency' | 'start_time' | 'alphabetical'

const URGENT_SHIFT_NAME_PRIORITIES = [
    'post door open',
    'before door open',
    'cloak',
    'luggage',
    'crowd',
    'jura',
    'juro'
]



type Props = {
    isOpen: boolean
    onClose: () => void
    volunteerId: number
    volunteerName: string
    eventIds: number[]
    onAllocationSuccess: () => void
}

export const QuickAllocateModal = (props: Props) => {
    const { isOpen, onClose, volunteerId, volunteerName, eventIds, onAllocationSuccess } = props
    const meta: QuickAllocateModalMetaProps = QuickAllocateModalMeta

    const [areas, setAreas] = useState<AreaAvailability[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [sortBy, setSortBy] = useState<SortOption>('urgency')
    const [searchQuery, setSearchQuery] = useState('')
    const [allocatingShiftId, setAllocatingShiftId] = useState<number | null>(null)
    const [successMessage, setSuccessMessage] = useState<string | null>(null)
    const [showOtherShifts, setShowOtherShifts] = useState(false)

    const fetchShiftsAvailability = useCallback(async () => {
        if (eventIds.length === 0) return

        setLoading(true)
        setError(null)

        try {
            const params = new URLSearchParams()
            if (volunteerId) {
                params.append('volunteer_id', volunteerId.toString())
            }

            // Fetch shifts from all selected events in parallel
            const responses = await Promise.all(
                eventIds.map(eventId =>
                    fetch(`${API_BASE_URL}/check-in/event/${eventId}/shifts-availability?${params}`, {
                        credentials: 'include'
                    })
                )
            )

            // Check for errors
            for (const response of responses) {
                if (!response.ok) {
                    const data = await response.json()
                    throw new Error(data.error || 'Failed to load shifts')
                }
            }

            // Parse all responses and merge areas
            const allAreas: AreaAvailability[] = []
            for (const response of responses) {
                const data: AreaAvailability[] = await response.json()
                allAreas.push(...data)
            }

            setAreas(allAreas)
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred')
        } finally {
            setLoading(false)
        }
    }, [eventIds, volunteerId])

    useEffect(() => {
        if (isOpen && eventIds.length > 0) {
            setShowOtherShifts(false)
            fetchShiftsAvailability()
        }
    }, [isOpen, eventIds, volunteerId, fetchShiftsAvailability])

    useEffect(() => {
        setShowOtherShifts(false)
    }, [sortBy, searchQuery])

    // Clear success message after delay
    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => setSuccessMessage(null), 3000)
            return () => clearTimeout(timer)
        }
    }, [successMessage])

    const formatTime = (timeStr: string) => {
        if (!timeStr) return ''
        const [hours, minutes] = timeStr.split(':')
        const hour = parseInt(hours, 10)
        const ampm = hour >= 12 ? 'PM' : 'AM'
        const hour12 = hour % 12 || 12
        return `${hour12}:${minutes} ${ampm}`
    }

    const formatTimeForApi = (timeStr: string) => {
        // Convert "HH:mm:ss" to "HH:mm"
        if (!timeStr) return ''
        const parts = timeStr.split(':')
        return `${parts[0]}:${parts[1]}`
    }

    const handleAllocate = async (shift: ShiftAvailability) => {
        setAllocatingShiftId(shift.id)
        setError(null)

        try {
            const response = await fetch(`${API_BASE_URL}/allocation/shift/${shift.id}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    volunteer: [volunteerId],
                    start_time: formatTimeForApi(shift.start_time),
                    end_time: formatTimeForApi(shift.end_time)
                })
            })

            if (!response.ok) {
                const data = await response.json()
                throw new Error(data.error || data.message || meta.allocationErrorMessage)
            }

            setSuccessMessage(meta.allocationSuccessMessage)

            // Refresh availability
            await fetchShiftsAvailability()

            // Notify parent to refresh allocations
            onAllocationSuccess()
        } catch (err) {
            setError(err instanceof Error ? err.message : meta.allocationErrorMessage)
        } finally {
            setAllocatingShiftId(null)
        }
    }

    // Helper to get conflict priority (0 = available, 1 = time conflict, 2 = already allocated)
    const getConflictPriority = (shift: ShiftAvailability): number => {
        if (shift.already_allocated) return 2
        if (shift.has_time_conflict) return 1
        return 0
    }

    const getUrgentNamePriority = (shift: ShiftAvailability): number => {
        const shiftName = `${shift.title || ''} ${shift.duty_name || ''}`.toLowerCase()
        return URGENT_SHIFT_NAME_PRIORITIES.some(priorityName => shiftName.includes(priorityName)) ? 1 : 0
    }

    // Sort and flatten shifts for display
    const getSortedShifts = (): Array<{ area: AreaAvailability; shift: ShiftAvailability }> => {
        const allShifts: Array<{ area: AreaAvailability; shift: ShiftAvailability }> = []

        for (const area of areas) {
            for (const shift of area.shifts) {
                allShifts.push({ area, shift })
            }
        }

        // Filter by search query
        const query = searchQuery.toLowerCase().trim()
        const filteredShifts = query
            ? allShifts.filter(({ area, shift }) => {
                const shiftName = (shift.title || shift.duty_name || '').toLowerCase()
                const areaName = area.name.toLowerCase()
                const eventName = (area.event_name || '').toLowerCase()
                return shiftName.includes(query) || areaName.includes(query) || eventName.includes(query)
            })
            : allShifts

        // Primary sort by conflict priority (available first, then time conflict, then already allocated)
        // Then by date (earlier dates first)
        // TODO: For multi-day events, area.date should be the area's specific date
        const sortByConflictAndDate = (a: { area: AreaAvailability; shift: ShiftAvailability }, b: { area: AreaAvailability; shift: ShiftAvailability }) => {
            // First: conflict priority
            const aPriority = getConflictPriority(a.shift)
            const bPriority = getConflictPriority(b.shift)
            if (aPriority !== bPriority) return aPriority - bPriority

            // Second: date (earlier dates first, null dates last)
            const aDate = a.area.date || ''
            const bDate = b.area.date || ''
            if (aDate !== bDate) return aDate.localeCompare(bDate)

            return 0 // Equal conflict status and date, will be sorted by secondary criteria
        }

        switch (sortBy) {
            case 'urgency':
                return filteredShifts.sort((a, b) => {
                    const primaryCompare = sortByConflictAndDate(a, b)
                    if (primaryCompare !== 0) return primaryCompare
                    const urgentNameCompare = getUrgentNamePriority(b.shift) - getUrgentNamePriority(a.shift)
                    if (urgentNameCompare !== 0) return urgentNameCompare
                    return b.shift.urgency_score - a.shift.urgency_score
                })
            case 'start_time':
                return filteredShifts.sort((a, b) => {
                    const primaryCompare = sortByConflictAndDate(a, b)
                    if (primaryCompare !== 0) return primaryCompare
                    const timeCompare = a.shift.start_time.localeCompare(b.shift.start_time)
                    if (timeCompare !== 0) return timeCompare
                    return a.area.name.localeCompare(b.area.name)
                })
            case 'alphabetical':
                return filteredShifts.sort((a, b) => {
                    const primaryCompare = sortByConflictAndDate(a, b)
                    if (primaryCompare !== 0) return primaryCompare
                    // Sort by shift name first (most visually prominent), then by area
                    const shiftNameA = a.shift.title || a.shift.duty_name || ''
                    const shiftNameB = b.shift.title || b.shift.duty_name || ''
                    const shiftCompare = shiftNameA.localeCompare(shiftNameB)
                    if (shiftCompare !== 0) return shiftCompare
                    return a.area.name.localeCompare(b.area.name)
                })
            default:
                return filteredShifts
        }
    }

    const sortedShifts = getSortedShifts()
    const hasPrioritizedShifts = sortBy === 'urgency' && sortedShifts.some(({ shift }) => getUrgentNamePriority(shift) > 0)
    const visibleShifts = hasPrioritizedShifts && !showOtherShifts
        ? sortedShifts.filter(({ shift }) => getUrgentNamePriority(shift) > 0)
        : sortedShifts
    const hiddenShiftCount = sortedShifts.length - visibleShifts.length

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={meta.title}>
            <div className="p-4">
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                    {meta.subtitle(volunteerName)}
                </p>

                {/* Search and Sort Controls */}
                <div className="flex flex-col sm:flex-row gap-3 mb-4">
                    {/* Search Input */}
                    <div className="flex-1">
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder={meta.searchPlaceholder}
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                        />
                    </div>
                    {/* Sort Controls */}
                    <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                            {meta.sortLabel}:
                        </label>
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value as SortOption)}
                            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        >
                            <option value="urgency">{meta.sortByUrgency}</option>
                            <option value="start_time">{meta.sortByStartTime}</option>
                            <option value="alphabetical">{meta.sortByAlphabetical}</option>
                        </select>
                    </div>
                </div>

                {/* Success Message */}
                {successMessage && (
                    <div className="mb-4 p-3 flex items-center gap-2 bg-success-50 dark:bg-success-900/20 border border-success-300 dark:border-success-700 rounded-lg text-success-700 dark:text-success-400">
                        <CheckCircleIcon className="w-5 h-5 flex-shrink-0" />
                        <span>{successMessage}</span>
                    </div>
                )}

                {/* Error Message */}
                {error && (
                    <div className="mb-4 p-3 flex items-center gap-2 bg-danger-50 dark:bg-danger-900/20 border border-danger-300 dark:border-danger-700 rounded-lg text-danger-700 dark:text-danger-400">
                        <XCircleIcon className="w-5 h-5 flex-shrink-0" />
                        <span>{error}</span>
                    </div>
                )}

                {/* Loading State */}
                {loading && (
                    <div className="py-8 text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                        <p className="mt-2 text-gray-500 dark:text-gray-400">{meta.loadingShiftsMessage}</p>
                    </div>
                )}

                {/* No Shifts State */}
                {!loading && sortedShifts.length === 0 && (
                    <div className="py-8 text-center text-gray-500 dark:text-gray-400">
                        {meta.noShiftsMessage}
                    </div>
                )}

                {/* Shifts List */}
                {!loading && sortedShifts.length > 0 && (
                    <div className="max-h-96 overflow-y-auto space-y-2">
                        {visibleShifts.map(({ area, shift }) => {
                            const shiftName = shift.title || shift.duty_name || 'Shift'
                            const spotsRemaining = shift.required_count - shift.checked_in_count
                            const isAlreadyAllocated = shift.already_allocated
                            const hasTimeConflict = shift.has_time_conflict && !isAlreadyAllocated
                            const isDimmed = isAlreadyAllocated

                            return (
                                <div
                                    key={shift.id}
                                    className={`p-3 rounded-lg border ${isAlreadyAllocated
                                            ? 'bg-gray-100 dark:bg-gray-900 border-gray-300 dark:border-gray-600 opacity-60'
                                            : hasTimeConflict
                                                ? 'bg-warn-50 dark:bg-warn-900/10 border-warn-200 dark:border-warn-800'
                                                : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                                        }`}
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className={`font-medium ${isDimmed ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-white'}`}>
                                                    {shiftName}
                                                </span>
                                                {isAlreadyAllocated && (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400">
                                                        <CheckCircleIcon className="w-3 h-3" />
                                                        {meta.alreadyAllocatedLabel}
                                                    </span>
                                                )}
                                                {hasTimeConflict && (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-warn-100 text-warn-700 dark:bg-warn-900/30 dark:text-warn-400">
                                                        <WarningIcon className="w-3 h-3" />
                                                        {meta.timeConflictLabel}
                                                    </span>
                                                )}
                                            </div>
                                            <div className={`flex items-center gap-1 text-sm mt-1 ${isDimmed ? 'text-gray-400 dark:text-gray-500' : 'text-gray-500 dark:text-gray-400'}`}>
                                                <LocationIcon className="w-4 h-4" />
                                                <span>{area.event_name ? `${area.event_name} • ${area.name}` : area.name}</span>
                                            </div>
                                            <div className={`flex items-center gap-1 text-sm ${isDimmed ? 'text-gray-400 dark:text-gray-500' : 'text-gray-500 dark:text-gray-400'}`}>
                                                <ClockIcon className="w-4 h-4" />
                                                <span>{formatTime(shift.start_time)} - {formatTime(shift.end_time)}</span>
                                            </div>
                                            <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs mt-2">
                                                <span className={isDimmed ? 'text-gray-400 dark:text-gray-500' : 'text-gray-600 dark:text-gray-400'}>
                                                    {meta.requiredLabel}: <span className="font-medium">{shift.required_count}</span>
                                                </span>
                                                <span className={isDimmed ? 'text-gray-400 dark:text-gray-500' : 'text-gray-600 dark:text-gray-400'}>
                                                    {meta.acceptedLabel}: <span className="font-medium">{shift.accepted_count}</span>
                                                </span>
                                                <span className={isDimmed ? 'text-gray-400 dark:text-gray-500' : 'text-gray-600 dark:text-gray-400'}>
                                                    {meta.checkedInLabel}: <span className="font-medium">{shift.checked_in_count}</span>
                                                </span>
                                                {!isDimmed && spotsRemaining > 0 && (
                                                    <span className="text-warn-600 dark:text-warn-400 font-medium">
                                                        ({spotsRemaining} needed)
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleAllocate(shift)}
                                            disabled={allocatingShiftId !== null || isAlreadyAllocated}
                                            className={`px-3 py-1.5 text-sm font-medium rounded-md focus:outline-none focus:ring-2 disabled:cursor-not-allowed flex-shrink-0 ${isAlreadyAllocated
                                                    ? 'text-gray-400 bg-gray-200 dark:bg-gray-700 dark:text-gray-500 cursor-not-allowed'
                                                    : hasTimeConflict
                                                        ? 'text-white bg-warn-600 hover:bg-warn-700 focus:ring-warn-500 disabled:opacity-50'
                                                        : 'text-white bg-primary-600 hover:bg-primary-700 focus:ring-primary-500 disabled:opacity-50'
                                                }`}
                                        >
                                            {allocatingShiftId === shift.id ? meta.allocatingButton : meta.allocateButton}
                                        </button>
                                    </div>
                                </div>
                            )
                        })}
                        {hiddenShiftCount > 0 && (
                            <button
                                type="button"
                                onClick={() => setShowOtherShifts(true)}
                                className="w-full px-3 py-2 text-sm font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-primary-900/20 dark:text-primary-300 dark:border-primary-800 dark:hover:bg-primary-900/30"
                            >
                                {meta.viewOtherShiftsButton(hiddenShiftCount)}
                            </button>
                        )}
                        {hasPrioritizedShifts && showOtherShifts && hiddenShiftCount === 0 && (
                            <button
                                type="button"
                                onClick={() => setShowOtherShifts(false)}
                                className="w-full px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-200 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                            >
                                {meta.hideOtherShiftsButton}
                            </button>
                        )}
                    </div>
                )}

                {/* Footer */}
                <div className="mt-6 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                    >
                        {meta.closeButton}
                    </button>
                </div>
            </div>
        </Modal>
    )
}
